/* eslint-disable no-undef */
/**
 * Google map API for store locator
 * -----------------------------------------------------------------------------
 *
 */

import InfoBox from './google-map-info-box'
import { calculateFlatDistance, initGeocoder, setDeviceMobile } from './helpers'

export default {

  bounds: null,
  map: null,
  mapTilesLoaded: false,
  mapStyles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ],
  locations: [],
  openMarkerId: null,
  markers: [],
  geocoder: null,
  visibleStoresCallback: null,
  // set to USA
  initialMapCoordinates: { lat: 39.8097343, lng: -98.5556199 },
  clusterer: null,
  isMobile: false,
  placesService: null,

  init() {
    this.addMap()
    this.startGeocoder()
    this.startPlaces()
  },

  /**
   * Init google map
   */
  async addMap() {
    if (!google) {
      return
    }

    const { Map } = await google.maps.importLibrary('maps')

    this.map = new Map(document.getElementById('uberall-locator-map'), {
      center: this.initialMapCoordinates,
      zoom: 3,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: this.mapStyles,
    })

    this.detectMapLoaded()
  },

  /**
   * Start geocoder service
   */
  async startGeocoder() {
    if (!google) {
      return
    }

    setDeviceMobile(this.isMobile)
    this.geocoder = await initGeocoder()
  },

  /**
   * Import and start Places service
   */
  async startPlaces() {
    await google.maps.importLibrary('places')

    this.placesService = new google.maps.places.PlacesService(this.map)
  },

  /**
   * Listen map load and start components
   */
  detectMapLoaded() {
    this.map.addListener('tilesloaded', () => {
      if (this.mapTilesLoaded) {
        return
      }

      this.mapTilesLoaded = true
      this.setAllMarkersOnMap()
      this.setMapBounds()
      this.setMarkerClusters()
      this.resetViewPort()
      this.setUpInfoBox()
      this.detectViewPortChange()
      this.detectMapClick()
    })
  },

  /**
   * Listen view port change and update store list
   */
  detectViewPortChange() {
    this.map.addListener('bounds_changed', () => {
      if (!this.visibleStoresCallback) {
        return
      }

      this.visibleStoresCallback(this.getStoresWithinViewPort())
    })
  },

  /**
   * Close info box on map click
   */
  detectMapClick() {
    this.map.addListener('click', () => {
      this.openMarkerId = null
      InfoBox.removeBox()
    })
  },

  /**
   * Init info box
   */
  setUpInfoBox() {
    InfoBox.build(
      { lat: 0.0, lng: 0.0 },
      document.getElementById('uberall-locator-info-box'),
    )
  },

  /**
   * Set locations data in variable
   * @param {Array} locations
   */
  setLocations(locations) {
    this.locations = locations
  },

  /**
   * Set markers on mpa for all stores
   */
  setAllMarkersOnMap() {
    this.locations.forEach((location) => {
      this.addMarker(location)
    })
  },

  /**
   * Center map
   */
  setMapBounds() {
    this.bounds = new google.maps.LatLngBounds()

    this.markers.forEach(marker => {
      this.bounds.extend(marker.getPosition())
    })
  },

  /**
   * Set clusters of markers on map when zoomed out
   */
  setMarkerClusters() {
    if (!markerClusterer) {
      return
    }

    const radius = this.isMobile ? 200 : 300
    const algorithm = new markerClusterer.SuperClusterAlgorithm({ maxZoom: 4, radius })
    const renderer = {
      render: ({ count, position }) => {
        const marker = new google.maps.Marker({
          label: {
            text: String(count),
            color: 'white',
          },
          position,
          icon: {
            url: `https://cdn.shopify.com/s/files/1/0089/4432/0612/files/transparent_cluser_map_icon_83d13d65-e98d-49cf-b372-12e126e3a89e.svg?v=1738841555`,
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        })

        marker.addListener('mouseover', () => {
          marker.setIcon('https://cdn.shopify.com/s/files/1/0089/4432/0612/files/Store_cluster_icon_-_hover.svg?v=1741781814')
        })

        marker.addListener('mouseout', () => {
          marker.setIcon('https://cdn.shopify.com/s/files/1/0089/4432/0612/files/transparent_cluser_map_icon_83d13d65-e98d-49cf-b372-12e126e3a89e.svg?v=1738841555')
        })

        return marker
      },
    }

    this.clusterer = new markerClusterer.MarkerClusterer({
      markers: this.markers,
      map: this.map,
      algorithm,
      renderer,
    })
  },

  /**
   * Update clusterer on new data
   */
  updateClusterer() {
    this.clusterer.clearMarkers()
    this.clusterer.addMarkers(this.markers)
  },

  /**
   * Remove existing markers from map
   */
  updateMarkers() {
    this.markers.forEach((marker) => {
      if (this.locations.some((location) => location.id === marker.id)) {
        marker.setMap(this.map)

        return
      }

      marker.setMap(null)
    })
  },

  /**
   * Add marker to the map per store
   * @param {Object} data
   */
  addMarker(data) {
    const coordinates = { lat: data.lat, lng: data.lng }
    const marker = new google.maps.Marker({
      position: coordinates,
      map: this.map,
      title: data.name,
      id: data.id,
      icon: {
        url: 'https://cdn.shopify.com/s/files/1/0089/4432/0612/files/Group_146.svg?v=1737545855',
      },
    })

    marker.addListener('click', () => {
      const location = this.locations.find((store) => store.id === marker.id)

      this.toggleMarkerInfoBox(location)
    })

    marker.addListener('mouseover', () => {
      marker.setIcon('https://cdn.shopify.com/s/files/1/0089/4432/0612/files/Store_pin_-_hover.svg?v=1741781814')
    })

    marker.addListener('mouseout', () => {
      marker.setIcon('https://cdn.shopify.com/s/files/1/0089/4432/0612/files/Group_146.svg?v=1737545855')
    })

    this.markers.push(marker)
  },

  /**
   * Show info box of store selected from search list
   * @param {Number} id
   */
  showSelectedStore(id) {
    const store = this.locations.find((location) => location.id === id)

    this.toggleMarkerInfoBox(store)
  },

  /**
   * Shows/hides store info box
   * @param {Object} data
   * @returns
   */
  toggleMarkerInfoBox(data) {

    /**
     * If clicked one is open - then close
     */
    if (this.openMarkerId === data.id) {
      this.removeInfoBox()

      return
    }

    /**
     * If clicked marker is new one or
     * If no open markers
     * close old one and show new
     */
    this.openMarkerId = data.id
    this.addInfoBox(data)
    this.map.setZoom(10)
    this.map.panTo({ lat: data.lat, lng: data.lng })
    this.map.panBy(0, 65)
  },

  /**
   * Remove info box from map
   */
  removeInfoBox() {
    this.openMarkerId = null
    InfoBox.removeBox()
  },

  /**
   * Open info box with store details
   * @param {Object} data
   */
  addInfoBox(data) {
    const coordinates = { lat: data.lat, lng: data.lng }
    const storeData = {
      title: data.name,
      street: data.streetAndNumber,
      address: {
        city: data.city,
        province: data.province,
        zip: data.zip,
      },
      phone: data.phone,
      distance: data.distance,
      openHours: data.openingHours,
      openNow: data.openNow,
      nextOpen: data.nextOpen,
    }

    InfoBox.removeBox()
    InfoBox.setNewPosition(coordinates)
    InfoBox.updateData(storeData)
    InfoBox.addBox(this.map)
  },

  /**
   * Get location data of search address
   *
   * Compile into Country / State / City data
   *
   * Call Places service
   */
  handleGeocode({ address }) {
    if (!this.geocoder || !this.placesService) {
      return
    }

    this.geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK') {
        const result = results[0]
        const placeName = result.address_components[0]
        const country = result.address_components.pop()
        const state = result.address_components.pop()
        const isState = result.types.includes('administrative_area_level_1')
        let query = `Veronica Beard store in ${placeName?.long_name}, country ${country?.long_name}`

        if (isState) {
          query = `${query}, province ${state?.long_name}`
        }

        this.callPlacesService(result, query, state)

        if (!this.visibleStoresCallback) {
          return
        }

        this.visibleStoresCallback(this.getStoresWithinViewPort())
      }
    })
  },

  /**
   * Call Places service with request built by Geolocation
   * @param {Object} request
   * @param {String} query
   * @param {Object} state
   */
  callPlacesService(result, query, state) {
    const request = {
      query,
      location: result.geometry.location,
    }

    this.placesService.textSearch(request, (resultsPlaces, statusPlaces) => {
      if (statusPlaces === google.maps.places.PlacesServiceStatus.OK) {

        /**
         * Check if results are Veronica Beard stores
         */
        const filtered = resultsPlaces.filter((place) => place.name.toLowerCase().includes('veronica beard'))

        /**
         * When stores are found, get location of each store
         * and fit the map viewport
         */
        if (filtered.length) {
          const bounds = new google.maps.LatLngBounds()

          filtered.forEach((place) => {
            bounds.extend(place.geometry.location)
          })

          this.map.fitBounds(bounds)

          /**
           * If only one store is found, zoom out
           * to view the store within its city
           */
          if (filtered.length === 1) {
            this.map.setZoom(13)
          }
        } else {
          this.checkUberallStores(result.geometry.location, state.short_name)
        }
      } else {
        this.checkUberallStores(result.geometry.location, state.short_name)
      }
    })
  },

  /**
   * If stores are not found, check if there is store
   * provided bu Uberall at the same location (province).
   *
   * Relocate map if store exists.
   *
   * This is mainly helpful for cases when store is not open yet,
   * and is to be opened in some future date. Places service might not
   * return results for this case
   *
   * @param {Object} locationCoords
   * @param {String} province
   */
  checkUberallStores(locationCoords, province) {
    const storeExists =
      this.locations.some((location) => location.province === province)

    if (storeExists) {
      this.map.fitBounds(this.bounds)
      this.map.setCenter(locationCoords)
      this.map.setZoom(10)
    }
  },

  /**
   * Zooming into user location when data is available
   *
   * @param {Object} location
   */
  zoomIntoUserLocation(location) {
    const markerPosition = { lat: this.locations[0]?.lat, lng: this.locations[0]?.lng }
    const distance = calculateFlatDistance(location, markerPosition)
    const distanceInMilesToCloseStore = 27

    if (distance > distanceInMilesToCloseStore) {
      this.map.setZoom(10)
      this.map.panTo(markerPosition)

      return
    }

    this.map.setZoom(10)
    this.map.panTo(location)
  },

  /**
   * Get ids visible stores on the map
   * @returns
   */
  getStoresWithinViewPort() {
    return this.markers
      .filter((marker) => this.map.getBounds().contains(marker.getPosition()))
      .map((store) => store.id)
  },

  /**
   * Recenter map to initial location
   */
  resetViewPort() {
    this.map.fitBounds(this.bounds)
    this.map.setZoom(3)
  },

}
